import { useState } from 'react';
import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { AchievementIcon } from 'components/achievements';
import {
  Badge,
  EmptyIndicator,
  FlexContainer,
  Pagination,
  Search,
  TitleWithIcon
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableRow
} from 'components/common/box';
import {
  Filters,
  FiltersActive,
  useAppliedFilterParams
} from 'components/common/filter';
import { ParticipantsStats } from 'components/common/stats';

import type {
  GetSupervisorAchievementsCategoriesRes,
  GetSupervisorAchievementsRes
} from 'shared-types/responses';

const FILTER_ID = 'my-team-assigned-achievements';

const MyTeamAchievementsAssigned = () => {
  const {
    messages,
    pageState: { supervisor },
    user
  } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const appliedFilters = useAppliedFilterParams(FILTER_ID);
  const categoryId = appliedFilters.categoryId || '';
  const [pageCategoryId, setPageCategoryId] = useState(categoryId);

  if (pageCategoryId !== categoryId) {
    setPageCategoryId(categoryId);
    setPage(1);
  }

  const queryString = new URLSearchParams({
    page: String(page),
    search,
    ...appliedFilters
  }).toString();

  const { data, loading } = useApiData<GetSupervisorAchievementsRes>({
    url: `/admin/supervisors/${user?.id}/achievements?${queryString}`,
    onlyIf: Boolean(user?.id)
  });

  const { data: achievementCategories } =
    useApiData<GetSupervisorAchievementsCategoriesRes>({
      url: `/admin/supervisors/${user?.id}/achievements-categories`,
      onlyIf: Boolean(user?.id)
    });

  const categoryFilters = achievementCategories?.categories?.length
    ? [
        {
          allowMultiple: true,
          field: 'categoryId',
          icon: 'tag' as const,
          items: achievementCategories.categories.map((category) => ({
            label: category.title,
            value: `${category.id}`
          })),
          position: 0,
          title: messages.commonCategories
        }
      ]
    : undefined;

  return (
    <>
      <Box
        loading={loading}
        padding={0}
        subHeaderContent={
          <FlexContainer
            style={{
              alignItems: 'flex-start',
              flexDirection: 'column',
              width: '100%'
            }}
          >
            <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
              <div style={{ width: 240 }}>
                <Search
                  placeholder={messages.achievementSearch}
                  onSearch={({ search: nextSearch }) => {
                    setPage(1);
                    setSearch(nextSearch);
                  }}
                  size="header"
                />
              </div>

              {categoryFilters ? (
                <Filters
                  customFilters={categoryFilters}
                  filterId={FILTER_ID}
                  filters={{}}
                  syncWithUrl={false}
                />
              ) : null}
            </FlexContainer>

            {categoryFilters ? <FiltersActive filterId={FILTER_ID} /> : null}
          </FlexContainer>
        }
        title={messages.myTeamAssignedAchievements}
        titleTooltip={messages.myTeamAssignedAchievementsInfo}
      >
        {!data?.achievements?.length ? (
          <EmptyIndicator
            icon="check-circle"
            title={messages.achievementsEmpty}
          />
        ) : (
          <BoxTable>
            {data?.achievements.map((achievement) => (
              <BoxTableRow key={achievement.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <FlexContainer style={{ gap: 15, minWidth: 250 }}>
                    <AchievementIcon size="small" type={achievement.type} />

                    <TitleWithIcon
                      title={achievement.title}
                      titleLink={
                        user?.roles?.includes('superadmin')
                          ? {
                              as: `/admin/achievements/${achievement.id}/supervisors/${user.id}`,
                              href: '/admin/achievements/[achievementId]/supervisors/[supervisorId]'
                            }
                          : {
                              as: `/admin/achievements/${achievement.id}/my-team`,
                              href: '/admin/achievements/[achievementId]/my-team'
                            }
                      }
                    >
                      {achievementTypes[achievement.type]?.title}
                    </TitleWithIcon>
                  </FlexContainer>
                </BoxTableCell>

                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  {achievement.numberOfPendingRequestsForSupervisor ? (
                    <Badge
                      category="error-light"
                      text={`${messages.commonPendingRequests}: ${achievement.numberOfPendingRequestsForSupervisor}`}
                    />
                  ) : null}
                </BoxTableCell>

                <BoxTableCell cellBreakpoint={breakpoints.medium} shrink={true}>
                  <div style={{ textAlign: 'right' }}>
                    <ParticipantsStats
                      completionEnabled={true}
                      notStartedLabel={messages.commonNotAdded}
                      progressData={{
                        completed: achievement.numberOfUsersCompleted,
                        started:
                          achievement.numberOfUsersCompleted +
                          achievement.numberOfCompletionsInProgress,
                        participants: supervisor.numberOfSupervisees
                      }}
                      showInProgress={[
                        'conversation',
                        'group-conversation',
                        'tasklist'
                      ].includes(achievement.type)}
                    />
                  </div>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page: nextPage }) => setPage(nextPage)}
      />
    </>
  );
};

export default MyTeamAchievementsAssigned;
